import { CloseButton, StyledButton, StyledDialog, StyledDialogTitle } from '@components/Styles/CommonStyle';
import { DialogActions, DialogContent, Button } from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import React from 'react';

/**
 * 기본 알럿, 컨펌, 중첩 다이얼로그
 * @param {Object} props
 * @param {string} props.type - 모달 타입 (alert, confirm, nested)
 * @param {boolean} props.open - 모달 열림/닫힘 상태
 * @param {function} props.onClose - 모달 닫기 핸들러
 * @param {string} props.title - 모달 제목
 * @param {string|function} props.message - 모달 내용
 * @param {boolean} props.showCloseButton - 우측 상단 닫기 버튼 표시 여부 (기본값: true)
 * @param {string} props.cancelText - 취소 버튼 텍스트 (기본값: '취소')
 * @param {string} props.confirmText - 확인 버튼 텍스트 (기본값: '확인')
 * @param {function} props.onConfirm - 확인 버튼 클릭 시 실행될 함수
 * @param {function} props.onCancel - 취소 버튼 클릭 시 실행될 함수
 * @param {boolean} props.disableBackdropClick - 백드롭 클릭 비활성화 여부 (기본값: false)
 * @param {number} props.zIndex - 모달 z-index (중첩 모달일 경우 사용)
 * @param {Object} props.nested - 중첩 모달 설정 (type이 'nested'일 때만 사용)
 * @param {string} props.nested.title - 중첩 모달 제목
 * @param {string|function} props.nested.message - 중첩 모달 내용
 * @param {string} props.nested.confirmText - 중첩 모달 확인 버튼 텍스트 (기본값: '확인')
 * @param {string} props.nested.cancelText - 중첩 모달 취소 버튼 텍스트 (기본값: '취소')
 * @param {string} props.nested.buttonText - 중첩 모달을 여는 버튼 텍스트 (기본값: '자세히 보기')
 * @param {function} props.nested.onConfirm - 중첩 모달 확인 버튼 클릭 시 실행될 함수
 * @param {function} props.nested.onCancel - 중첩 모달 취소 버튼 클릭 시 실행될 함수
 */
const CustomDialog = ({
  type = 'alert',
  open,
  onClose,
  title,
  message,
  showCloseButton = true,
  cancelText = '취소',
  confirmText = '확인',
  onConfirm,
  onCancel,
  disableBackdropClick = false,
  zIndex,
  nested,
}) => {
  const [isNestedOpen, setIsNestedOpen] = React.useState(false);

  const handleBackdropClick = (event) => {
    // 모달 내부 클릭 시 이벤트 무시
    if (event.target === event.currentTarget) {
      if (disableBackdropClick) {
        event.stopPropagation();
        return;
      }
      onClose();
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onClose();
  };

  const handleNestedOpen = (e) => {
    e.stopPropagation();
    setIsNestedOpen(true);
  };

  const handleNestedClose = () => {
    setIsNestedOpen(false);
  };

  const renderNestedButton = () => {
    if (!nested || type !== 'nested') return null;

    return (
      <Button variant="contained" color="primary" size="small" sx={{ mt: 2 }} onClick={handleNestedOpen}>
        {nested.buttonText || '자세히 보기'}
      </Button>
    );
  };

  const renderMessage = () => {
    const content = typeof message === 'function' ? message() : message;
    return (
      <>
        {content}
        {renderNestedButton()}
      </>
    );
  };

  const renderActions = () => {
    // alert 타입일 경우 확인 버튼만 표시
    if (type === 'alert') {
      return (
        <StyledButton variant="contained" color="primary" onClick={handleConfirm}>
          {confirmText}
        </StyledButton>
      );
    }

    // confirm 또는 nested 타입일 경우 취소, 확인 버튼 모두 표시
    return (
      <>
        <StyledButton variant="outlined" onClick={handleCancel}>
          {cancelText}
        </StyledButton>
        <StyledButton variant="contained" color="primary" onClick={handleConfirm}>
          {confirmText}
        </StyledButton>
      </>
    );
  };

  return (
    <React.Fragment>
      <StyledDialog
        open={open}
        onClose={onClose}
        aria-labelledby="modal-title"
        disableRestoreFocus
        onClick={handleBackdropClick}
        sx={{
          '& .MuiDialog-paper': {
            zIndex: zIndex || 'auto',
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
          },
        }}
      >
        <StyledDialogTitle id="modal-title">{title}</StyledDialogTitle>

        {showCloseButton && (
          <CloseButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        )}

        <DialogContent>{renderMessage()}</DialogContent>

        <DialogActions>{renderActions()}</DialogActions>
      </StyledDialog>

      {/* 중첩 모달 */}
      {type === 'nested' && nested && (
        <CustomDialog
          type="confirm"
          open={isNestedOpen}
          onClose={handleNestedClose}
          title={nested.title}
          message={nested.message}
          confirmText={nested.confirmText}
          cancelText={nested.cancelText}
          onConfirm={nested.onConfirm}
          onCancel={nested.onCancel}
          zIndex={1500}
          disableBackdropClick={true}
        />
      )}
    </React.Fragment>
  );
};

export default CustomDialog;
